import React, { useState } from 'react'
import { useToast } from '~/lib/ui/toast'
import Spinner from '~/components/shared/Spinner'
import { apiPost } from '~/util/api'
import { useDropzone } from 'react-dropzone'
import { cn } from '~/lib/utils'
import { useTranslation } from '~/hooks/useTranslation'
import { Button } from '~/lib/ui/button'

const ResumeUpload = ({ className, helpText, ...props }) => {
  const {
    containerClassName,
    resumeReview,
    setUploadedResumeId,
    revisionRequest,
    serviceRequestForm,
    translationKey,
    mostRecentResume,
  } = props
  const { t } = useTranslation(translationKey || 'resume_upload')
  const [resumeIsUploading, setResumeIsUploading] = useState(false)
  const [fileName, setFileName] = useState('')
  const [resumeFieldsArePopulated, setResumeFieldsArePopulated] =
    useState(false)
  const [isDraggingOver, setIsDraggingOver] = useState(false)
  const [selectedExistingResume, setSelectedExistingResume] = useState(false)
  const { toast } = useToast()
  const { getRootProps, getInputProps } = useDropzone({
    maxFiles: 1,
    accept: {
      '*/*/pdf': ['.pdf'],
      '*/*/msword': ['.doc'],
      '*/*/document': ['.docx'],
    },
    onDropAccepted: (incomingFiles) => saveNewResume(incomingFiles[0]),
    onDropRejected: (incomingFiles) => rejectionMessage(incomingFiles),
    onDragEnter: () => setIsDraggingOver(true),
    onDragLeave: () => setIsDraggingOver(false),
    onDrop: () => setIsDraggingOver(false),
    maxSize: 15_000_000, // 15MB
    disabled: resumeIsUploading,
  })

  const rejectionMessage = (incomingFiles) => {
    const {
      file: { type },
      errors,
    } = incomingFiles[0]
    const description = errors
      .map((e) => ` • ${t(e.code.replaceAll('-', '_'))}`)
      .join('\n')
    console.error('File upload rejected:', description)
    if (type) {
      toast({
        description,
        title: t('could_not_upload_resume'),
        variant: 'destructive',
        duration: 10_000,
      })
    }
  }

  const saveNewResume = async (file) => {
    setResumeIsUploading(true)
    const name = file.name.split('.')[0]
    const formData = new FormData()
    if (resumeReview) {
      if (revisionRequest) {
        serviceRequestForm.setValue('remote_content', file)
        setResumeIsUploading(false)
        setResumeFieldsArePopulated(true)
        setFileName(name)
      } else {
        formData.append('imported_resume', file)
        try {
          const response = await apiPost(
            '/coach_services/upload_resume',
            formData,
            true,
          )
          setResumeIsUploading(false)
          setUploadedResumeId(response.data.document_id)
          setFileName(name)
          setResumeFieldsArePopulated(true)
        } catch (e) {
          setResumeIsUploading(false)
          console.error('Error saving new resume', e)
          toast({
            description: t('resume_upload_failed'),
            variant: 'destructive',
          })
          return
        }
      }
    }
  }

  const useMostRecentResume = (e) => {
    if (mostRecentResume) {
      setUploadedResumeId(mostRecentResume.id)
      setFileName(mostRecentResume.name)
      setResumeFieldsArePopulated(true)
      setSelectedExistingResume(true)
    }
    e.preventDefault()
  }

  const baseRootClassName = cn(
    'min-h-[20rem] p-4 border-dashed border text-center content-center justify-items-center',
    className,
  )
  const defaultRootClassName = isDraggingOver
    ? baseRootClassName + ' border-primary border-2 bg-lily-blue'
    : baseRootClassName
  const rootClassName = resumeIsUploading
    ? defaultRootClassName
    : defaultRootClassName + ' cursor-pointer'

  return (
    <div className={cn('px-4 pb-4', containerClassName)}>
      <div className="justify-left mb-4 flex flex-row items-center">
        <label>
          {revisionRequest
            ? t('upload_resume_not_required')
            : t('upload_resume')}
        </label>
      </div>
      {mostRecentResume && (
        <div className="mb-4">
          <Button className="mr-2" onClick={useMostRecentResume}>
            {selectedExistingResume
              ? t('selected_this_resume')
              : t('use_most_recent')}
          </Button>
          <a
            href={`/resume/builder/${mostRecentResume?.id}`}
            className="text-link"
          >
            {mostRecentResume?.name}
          </a>
        </div>
      )}
      <div
        {...getRootProps({
          className: rootClassName,
        })}
      >
        <input {...getInputProps()} />
        {resumeIsUploading && <Spinner show={true} className="w-14" />}
        {!resumeIsUploading && {
          ...(resumeFieldsArePopulated ? (
            <div>
              <p>{t('resume_uploaded_successfully')}</p>
              <p>{fileName}</p>
            </div>
          ) : (
            <div>
              <p>{helpText || t('drag_resume_here_or_click_to_select')}</p>
              <em>{t('file_invalid_type')}</em>
            </div>
          )),
        }}
      </div>
    </div>
  )
}
export default ResumeUpload
